import { Component, computed, effect, inject, input, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { PersonGridItemObject } from '@it2go/types';
import { TranslatePipe } from '@ngx-translate/core';
import { WorkParticipantsSelectStore } from './work-participants-select.store';
import { getFolderPersonHistoryFrag } from './work-participants-select.query';
import {
    MultiSelectInputComponent,
} from '../../../kit/input/multi-select-input/multi-select-input.component';
import { UserChipComponent } from '../../../kit/user-chip/user-chip.component';
import { UserChipRenderTypeEnum } from '../../../kit/user-chip/user-chip.component.types';
import { formatPerson, FormatPersonPipe } from '../../../../modules/shared/pipes/format-person.pipe';
import { FilterBuilder } from '../../../../modules/content/helper/filter.builder';
import { AppFormGroup } from '../../../../utils/form.utils';
import { isWorkParticipantsEnabled } from '../../../../utils/work.utils';

/** Složka tak, jak ji formuláře prací mají po ruce - vlastník slouží jen jako popisek skupiny. */
export type WorkParticipantsFolder = {
    id: number;
    person?: PersonGridItemObject | null;
};

type ParticipantOption = PersonGridItemObject & { label: string };
type ParticipantGroup = { label: string; items: ParticipantOption[] };

/**
 * Účastníci práce - osoby, které byly ve složce v době konání práce.
 *
 * Nabídka se čte z historie pobytů (`folderPerson.history` + `activeAt`), ne z aktuálního
 * členství: u starší práce se musí nabídnout ti, kdo ve složce byli tehdy. Načítá se po jedné
 * složce, protože `FolderPersonService.scopeToFolder` záměrně vynucuje právě jedno `folderId` -
 * je to bezpečnostní zábrana, ne omezení, které by se dalo obejít.
 *
 * Formuláře, které zakládají práci nad více složkami (Skupinová aktivita, Práce hromadně),
 * ukazují jeden plochý select seskupený po složkách a při ukládání si výběr rozdělí podle
 * `folderIdByPersonId()`.
 */
@Component({
    selector: 'app-work-participants-select',
    templateUrl: './work-participants-select.component.html',
    providers: [WorkParticipantsSelectStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MultiSelectInputComponent,
        UserChipComponent,
        FormatPersonPipe,
        TranslatePipe,
    ],
})
export class WorkParticipantsSelectComponent {

    protected readonly store = inject(WorkParticipantsSelectStore);

    public readonly form = input.required<AppFormGroup>();
    public readonly id = input<string>('participantIds');
    public readonly label = input<string>('');
    public readonly serviceId = input<number | null | undefined>(null);
    public readonly folders = input<WorkParticipantsFolder[]>([]);
    public readonly atDate = input<Date | null | undefined>(null);
    /** Nová práce se předvyplní všemi osobami složky; u editace existující práce nechat vypnuté. */
    public readonly autoSelectAll = input<boolean>(false);

    /** Odpovědi z historie pobytů, po složkách. */
    private readonly personsByFolder = signal<Record<number, PersonGridItemObject[]>>({});
    /** Nabídka, pro kterou už se předvyplňovalo - aby se ruční odebrání účastníka nevracelo zpět. */
    private autoSelectedFor: string | null = null;

    public readonly enabled = computed(() => isWorkParticipantsEnabled(
        this.store.globalGlobalStore.organizationStore.getServiceById(this.serviceId() || 0)(),
    ));

    /** Ke které složce účastník patří - podle toho se výběr rozděluje mezi zakládané práce. */
    public readonly folderIdByPersonId = computed<Map<number, number>>(() => {
        const map = new Map<number, number>();
        Object.entries(this.personsByFolder()).forEach(([folderId, persons]) => {
            persons.forEach((it) => map.set(Number(it.id), Number(folderId)));
        });

        return map;
    });

    protected readonly hasGroups = computed(() => this.folderIds().length > 1);

    protected readonly options = computed<ParticipantOption[] | ParticipantGroup[]>(() => {
        const byFolder = this.personsByFolder();
        const folders = this.folders().filter((it) => it?.id);

        if (!this.hasGroups()) {
            return this.toOptions(byFolder[folders[0]?.id] || []);
        }

        return folders
            .map((folder) => ({
                label: formatPerson(folder.person) || String(folder.id),
                items: this.toOptions(byFolder[folder.id] || []),
            }))
            .filter((it) => it.items.length)
        ;
    });

    private readonly folderIds = computed(() => Array.from(new Set(
        this.folders().map((it) => Number(it?.id)).filter((it) => it),
    )));

    private readonly allPersonIds = computed(() => Object
        .values(this.personsByFolder())
        .flatMap((persons) => persons.map((it) => Number(it.id)))
    );

    /** Den, ne přesný čas - jinak by se přenačítalo při každém ťuknutí do času práce. */
    private readonly atDayKey = computed(() => (this.atDate() || new Date()).toDateString());

    constructor() {
        effect(() => {
            const folderIds = this.folderIds();
            const at = this.atDayKey();
            const enabled = this.enabled();

            untracked(() => this.loadPersons(enabled ? folderIds : [], at));
        });

        effect(() => {
            const available = this.allPersonIds();
            const autoSelect = this.autoSelectAll();

            untracked(() => this.syncSelection(available, autoSelect));
        });
    }

    /**
     * Nabídka se mění (jiné složky, jiné datum práce) a výběr ji musí následovat: kdo v nabídce
     * není, ten ve složce k datu práce nebyl a server by ho stejně odmítl. Předvyplňuje se jen
     * jednou pro danou nabídku, jinak by se ručně odebraný účastník hned vracel zpátky.
     */
    private syncSelection(available: number[], autoSelect: boolean): void {
        const form = this.form();
        const id = this.id();
        const allowed = new Set(available);
        const key = [...allowed].sort().join(',');

        if (autoSelect && allowed.size && this.autoSelectedFor !== key) {
            this.autoSelectedFor = key;
            form.patchValueCode({ [id]: [...allowed] } as any);

            return;
        }
        if (!allowed.size) {
            this.autoSelectedFor = null;
        }

        const selected: number[] = form.getRawValue()?.[id] || [];
        const kept = selected.filter((it) => allowed.has(Number(it)));
        if (kept.length !== selected.length) {
            form.patchValueCode({ [id]: kept } as any);
        }
    }

    private toOptions(persons: PersonGridItemObject[]): ParticipantOption[] {
        return persons.map((it) => ({ ...it, label: formatPerson(it) }));
    }

    private async loadPersons(folderIds: number[], at: string): Promise<void> {
        if (!folderIds.length) {
            this.personsByFolder.set({});

            return;
        }

        const activeAt = new Date(at).toISOString();
        const loaded = await Promise.all(folderIds.map(async (folderId) => {
            const res = await this.store.api.getFolderPersonHistory(
                new FilterBuilder()
                    .folderId(folderId)
                    .where('activeAt', activeAt)
                    .where('alive', true)
                    .limit(999)
                    .create(),
                getFolderPersonHistoryFrag,
            );

            return [
                folderId,
                (res?.items || []).map((it: any) => it.person).filter((it: any) => it),
            ] as const;
        }));

        this.personsByFolder.set(Object.fromEntries(loaded));
    }

    protected readonly UserChipRenderTypeEnum = UserChipRenderTypeEnum;
}
